<template>
  <!-- Add Ticket -->
  <div class="modal fade dq-ticket-modal" id="add_ticket" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable dq-ticket-dialog"
    >
      <div class="modal-content dq-modal-content" :key="modalKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-ticket"></i>
            </div>

            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Add Ticket</h4>
              <div class="dq-subtitle">
                Log a support request, assign an agent, and track progress.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitForm">
          <div class="modal-body dq-modal-body">
            <!-- Top quick tips row -->
            <div class="dq-info-row">
              <div class="dq-info-pill">
                <i class="ti ti-bolt"></i>
                <span>Tip: Use a clear subject for faster resolution.</span>
              </div>
              <div class="dq-info-pill">
                <i class="ti ti-user-check"></i>
                <span>Assign an agent to start tracking immediately.</span>
              </div>
            </div>

            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-pencil"></i> Ticket Details
                </div>
                <div class="dq-card-sub">
                  Fill the essentials — subject, category, priority and status.
                </div>
              </div>

              <div class="row g-3 mt-0">
                <!-- Title -->
                <div class="col-12">
                  <label class="form-label dq-label">Title</label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="form-control dq-input"
                    placeholder="e.g. Cannot access payroll module"
                  />
                  <div class="dq-help">
                    Optional. If empty, Subject will be used.
                  </div>
                </div>

                <!-- Category -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Event Category</label>
                  <div class="dq-vselect">
                    <vue-select
                      v-model="selectedCategoryName"
                      :options="categoryNames"
                      id="redist"
                      placeholder="Select category"
                    />
                  </div>
                  <div class="dq-help">
                    Helps route the ticket to the right queue.
                  </div>
                </div>

                <!-- Assign To -->
                <!-- Assign To -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Assign To</label>

                  <div class="dropdown w-100 dq-emp-dd">
                    <button
                      id="assignee-dd-btn"
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                      type="button"
                      data-bs-toggle="dropdown"
                      :disabled="saving || !isTicketStaff"
                      @click="onAssigneeDropdownOpen"
                    >
                      <span class="text-truncate">{{
                        selectedAssigneeLabel
                      }}</span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div
                      class="dropdown-menu p-2 w-100 shadow dq-dd-menu"
                      @click.stop
                    >
                      <input
                        ref="assigneeSearchRef"
                        v-model="assigneeQuery"
                        type="text"
                        class="form-control mb-2 dq-input"
                        placeholder="Search agent name..."
                        autocomplete="off"
                      />

                      <div class="dq-search-list">
                        <button
                          v-for="opt in filteredAssignees"
                          :key="opt.id"
                          type="button"
                          class="dropdown-item text-truncate dq-dd-item"
                          @click="chooseAssignee(opt)"
                        >
                          <div class="fw-semibold text-truncate">
                            {{ opt.text }}
                          </div>
                        </button>

                        <div
                          v-if="filteredAssignees.length === 0"
                          class="text-center py-2 small text-muted"
                        >
                          No agent found
                        </div>
                      </div>

                      <div
                        class="d-flex justify-content-between align-items-center pt-2 border-top mt-2"
                      >
                        <button
                          type="button"
                          class="btn btn-sm btn-light"
                          @click="clearAssignee"
                        >
                          Clear
                        </button>

                        <span class="small text-muted"
                          >{{ filteredAssignees.length }} result(s)</span
                        >
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">
                    {{
                      isTicketStaff
                        ? "Optional. Leave empty to assign later."
                        : "Requester tickets are submitted unassigned and picked up by support staff."
                    }}
                  </div>
                </div>

                <!-- Subject -->
                <div class="col-12">
                  <label class="form-label dq-label">
                    Subject <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model="form.subject"
                    type="text"
                    class="form-control dq-input"
                    placeholder="Enter subject"
                  />
                  <div class="dq-help">
                    This is the main title used on listings and exports.
                  </div>
                </div>

                <!-- Description -->
                <div class="col-12">
                  <label class="form-label dq-label">Ticket Description</label>
                  <textarea
                    v-model="form.description"
                    class="form-control dq-input"
                    rows="4"
                    placeholder="Describe the issue, steps to reproduce, and expected behavior..."
                  ></textarea>
                </div>

                <!-- Priority -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Priority</label>
                  <div class="dq-vselect">
                    <vue-select
                      v-model="selectedPriority"
                      :options="priorityOptions"
                      id="medispr"
                      placeholder="Select priority"
                    />
                  </div>

                  <div
                    class="dq-chip mt-2"
                    :class="priorityChipClass(selectedPriority)"
                  >
                    <i class="ti ti-flag"></i>
                    <span>{{ priorityChipText(selectedPriority) }}</span>
                  </div>
                </div>

                <!-- Status -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Status</label>
                  <div class="dq-vselect">
                    <vue-select
                      v-model="selectedStatus"
                      :options="statusOptions"
                      id="closetic"
                      placeholder="Select status"
                    />
                  </div>

                  <div
                    class="dq-chip mt-2"
                    :class="statusChipClass(selectedStatus)"
                  >
                    <i class="ti ti-progress"></i>
                    <span>{{ statusChipText(selectedStatus) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <a
              href="javascript:void(0);"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :class="{ disabled: saving }"
              >Cancel</a
            >

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Add Ticket <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add Ticket -->

  <!-- Delete Modal (premium UI; still same id) -->
  <div class="modal fade dq-ticket-modal" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div class="dq-headtext">
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action can’t be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete selected items?</div>
              <div class="dq-danger-sub">
                You want to delete all the marked items. This can’t be undone
                once you delete.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <a
            href="javascript:void(0);"
            class="btn btn-light dq-btn dq-btn-muted me-2"
            data-bs-dismiss="modal"
            >Cancel</a
          >
          <router-link
            to="/tickets/ticket"
            class="btn btn-danger dq-btn dq-btn-danger"
          >
            Yes, Delete <i class="ti ti-trash ms-2"></i>
          </router-link>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
</template>

<script setup>
/**
 * Dynamic ticket creation modal:
 * - Categories from Pinia store
 * - Assignee from employees via vue-select -> assignee_id
 * - Emits 'saved' after successful create
 */
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useTicketStore } from "@/stores/ticketStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useTicketCategoryStore } from "@/stores/ticketCategoryStore";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const emit = defineEmits(["saved"]);

const ticketStore = useTicketStore();
const empStore = useEmployeeStore();
const catStore = useTicketCategoryStore();
const authStore = useAuthStore();

const saving = computed(() => ticketStore.loading);
const modalKey = ref(0);
const isTicketStaff = computed(
  () =>
    authStore.can("Ticket.Edit") ||
    authStore.can("Ticket.Delete") ||
    authStore.can("Ticket.Write")
);

// --- Form state (UI) ---
const form = ref({
  title: "",
  subject: "",
  description: "",
  category_id: "",
  assignee_id: "",
  requester_id: "", // filled via current employee (or fallback)
  priority: "Low",
  status: "Open",
});

// Options
const priorityOptions = ["Select", "High", "Low", "Medium", "Urgent"];
const statusOptions = [
  "Select",
  "Closed",
  "Reopened",
  "Open",
  "On Hold",
];

// vue-select bound values
const selectedCategoryName = ref(null);
const selectedPriority = ref("Select");
const selectedStatus = ref("Select");

// Employees for "Assign To"
const empOptions = computed(() =>
  (empStore.employees || []).map((e) => {
    const u = e.user || {};
    return {
      id: e.id,
      text: [u.first_name || e.first_name, u.last_name || e.last_name]
        .filter(Boolean)
        .join(" "),
    };
  })
);

// Categories from store
const categoryNames = computed(() => [
  "Select",
  ...catStore.items.map((c) => c.name),
]);
function categoryIdFromName(name) {
  if (!name || name === "Select") return null;
  return catStore.findIdByName(name);
}

// Map UI values to backend-safe ones
function mapStatusToBackend(v) {
  if (!v || v === "Select") return "Open";
  if (v.toLowerCase() === "inprogress") return "Open";
  return v;
}
function mapPriorityToBackend(v) {
  if (!v || v === "Select") return "Low";
  return v;
}

function resolveRequesterId() {
  if (authStore.user?.employee?.id) return authStore.user.employee.id;
  const list = empStore.employees || [];
  return list[0]?.id || null;
}

function closeModal() {
  document.querySelector('#add_ticket [data-bs-dismiss="modal"]')?.click();
}

// Submit
async function submitForm() {
  const payload = {
    subject: form.value.subject?.trim() || form.value.title?.trim() || "",
    description: form.value.description || "",
    category_id: categoryIdFromName(selectedCategoryName.value),
    assignee_id: form.value.assignee_id || null,
    requester_id: resolveRequesterId(),
    priority: mapPriorityToBackend(selectedPriority.value),
    status: mapStatusToBackend(selectedStatus.value),
    visibility: "Public",
  };

  if (!payload.subject) {
    toast("Please enter Title or Subject", "error");
    return;
  }
  if (!payload.requester_id) {
    toast("No requester found. Please ensure employees are loaded.", "error");
    return;
  }

  try {
    await ticketStore.create(payload);
    toast("Ticket created", "success");
    resetForm();
    closeModal();
    emit("saved");
  } catch (_) {
    // errors surfaced by store/toast
  }
}

// ===== Assign To dropdown (searchable) =====
const assigneeQuery = ref("");
const assigneeSearchRef = ref(null);

const selectedAssigneeLabel = computed(() => {
  if (!form.value.assignee_id) return "Select agent";
  const hit = empOptions.value.find(
    (e) => Number(e.id) === Number(form.value.assignee_id)
  );
  return hit?.text || "Select agent";
});

const filteredAssignees = computed(() => {
  const q = assigneeQuery.value.trim().toLowerCase();
  if (!q) return empOptions.value.slice(0, 10); // limit to 1st 50
  return empOptions.value.filter((e) =>
    String(e.text || "")
      .toLowerCase()
      .includes(q)
  );
});

function onAssigneeDropdownOpen() {
  nextTick(() => {
    assigneeSearchRef.value?.focus?.();
  });
}

function chooseAssignee(opt) {
  if (!isTicketStaff.value) return;
  form.value.assignee_id = opt?.id || null; // ✅ same field used by submit payload
  assigneeQuery.value = "";
  // close dropdown (bootstrap)
  document.getElementById("assignee-dd-btn")?.click();
}

function clearAssignee() {
  if (!isTicketStaff.value) return;
  form.value.assignee_id = null;
  assigneeQuery.value = "";
  document.getElementById("assignee-dd-btn")?.click();
}

// Reset & lifecycle
function resetForm() {
  form.value = {
    title: "",
    subject: "",
    description: "",
    category_id: "",
    assignee_id: "",
    requester_id: "",
    priority: "Low",
    status: "Open",
  };
  assigneeQuery.value = ""; // ✅ add

  selectedCategoryName.value = null;
  selectedPriority.value = "Select";
  selectedStatus.value = "Select";
  modalKey.value++;
}

function priorityChipClass(v) {
  const x = String(v || "").toLowerCase();
  if (x === "urgent" || x === "high") return "dq-chip-danger";
  if (x === "medium") return "dq-chip-warn";
  if (x === "low") return "dq-chip-ok";
  return "dq-chip-muted";
}
function priorityChipText(v) {
  if (!v || v === "Select") return "Default: Low";
  return `Priority: ${v}`;
}

function statusChipClass(v) {
  const x = String(v || "").toLowerCase();
  if (x === "closed") return "dq-chip-ok";
  if (x === "reopened") return "dq-chip-danger";
  if (x === "on hold") return "dq-chip-warn";
  if (x === "inprogress" || x === "open") return "dq-chip-info";
  return "dq-chip-muted";
}
function statusChipText(v) {
  if (!v || v === "Select") return "Default: Open";
  return `Status: ${v}`;
}

let cleanup = null;
onMounted(async () => {
  if (
    !empStore.employees?.length &&
    typeof empStore.fetchEmployees === "function"
  ) {
    await empStore.fetchEmployees();
  }
  if (!catStore.items.length) {
    await catStore.fetchAll();
  }

  const el = document.getElementById("add_ticket");
  const onShow = () => resetForm();
  const onHidden = () => resetForm();

  el?.addEventListener("show.bs.modal", onShow);
  el?.addEventListener("hidden.bs.modal", onHidden);

  cleanup = () => {
    el?.removeEventListener("show.bs.modal", onShow);
    el?.removeEventListener("hidden.bs.modal", onHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* ===== Shell ===== */
.dq-ticket-dialog {
  max-width: 920px;
}
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

.dq-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.16), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}

.dq-modal-body {
  padding: 16px 20px;
}

/* Reliable scroll + sticky footer */
.modal-dialog.modal-dialog-scrollable .modal-content {
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-dialog.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  max-height: calc(92vh - 150px);
  overscroll-behavior: contain;
}
.dq-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icons */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Info row */
.dq-info-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.dq-info-pill {
  flex: 1 1 260px;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.72);
  font-weight: 700;
  font-size: 0.82rem;
}
.dq-info-pill i {
  color: rgba(99, 102, 241, 0.9);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-chip-muted {
  background: rgba(15, 23, 42, 0.04);
  color: rgba(15, 23, 42, 0.7);
}
.dq-chip-ok {
  background: rgba(16, 185, 129, 0.12);
  color: rgb(5, 150, 105);
}
.dq-chip-warn {
  background: rgba(245, 158, 11, 0.12);
  color: rgb(217, 119, 6);
}
.dq-chip-danger {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}
.dq-chip-info {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}

/* ===== vue-select premium alignment =====
   NOTE: vue-select uses .vs__* classes */
.dq-vselect :deep(.vs__dropdown-toggle) {
  border-radius: 14px;
  padding: 6px 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  min-height: 42px;
}
.dq-vselect :deep(.vs__selected-options) {
  padding-left: 2px;
}
.dq-vselect :deep(.vs__search),
.dq-vselect :deep(.vs__search:focus) {
  margin: 0;
}
.dq-vselect :deep(.vs__dropdown-menu) {
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
}
.dq-vselect :deep(.vs__open-indicator) {
  transform: scale(0.95);
}
.dq-vselect :deep(.vs__clear) {
  margin-right: 6px;
}
.dq-vselect :deep(.vs--open .vs__dropdown-toggle) {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}

/* Mobile */
@media (max-width: 576px) {
  .dq-ticket-dialog {
    max-width: 96vw;
    margin: 0.75rem auto;
  }
}
</style>
